import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { FaPlay } from 'react-icons/fa';

const VideoGallery = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const videos = [
    {
      title: 'Jump',
      description: 'Energia pura no trampolim!',
      url: 'https://firebasestorage.googleapis.com/v0/b/fenixforce-b6b29.appspot.com/o/VID-20251201-WA0031.mp4?alt=media&token=3f38de0d-bc3e-41f3-b418-34576aa79d8e',
      delay: '0s'
    },
    {
      title: 'Spinning',
      description: 'Pedale rumo aos seus objetivos!',
      url: 'https://firebasestorage.googleapis.com/v0/b/fenixforce-b6b29.appspot.com/o/VID-20251201-WA0032.mp4?alt=media&token=541ee6f6-5bb8-40ca-b034-65f306914f08',
      delay: '0.2s'
    },
    {
      title: 'Funcional Noite',
      description: 'Treino completo para resultados reais!',
      url: 'https://firebasestorage.googleapis.com/v0/b/fenixforce-b6b29.appspot.com/o/VID-20251201-WA0033.mp4?alt=media&token=758ae3b0-66b9-473e-ab4a-ba3373513680',
      delay: '0.4s'
    }
  ];

  return (
    <Container id="videos" ref={sectionRef}>
      <BackgroundGlow />
      
      <Content>
        <HeaderSection $isVisible={isVisible}>
          <SectionBadge>
            <span>🎬 AULAS AO VIVO</span>
          </SectionBadge>
          <Title>
            VEJA NOSSOS <HighlightText>TREINOS</HighlightText> EM AÇÃO
          </Title>
          <Subtitle>
            Sinta a energia das nossas aulas e venha fazer parte dessa transformação
          </Subtitle>
        </HeaderSection>

        <VideosGrid>
          {videos.map((video, index) => (
            <VideoCard 
              key={index}
              $delay={video.delay}
              $isVisible={isVisible}
            >
              <CardGlow />
              <VideoWrapper>
                <StyledVideo 
                  controls
                  preload="metadata"
                  playsInline
                  poster=""
                >
                  <source src={video.url} type="video/mp4" />
                  Seu navegador não suporta vídeos.
                </StyledVideo>
                <PlayOverlay className="play-overlay">
                  <PlayButton>
                    <FaPlay />
                  </PlayButton>
                </PlayOverlay>
              </VideoWrapper>
              <VideoInfo>
                <VideoTitle>{video.title}</VideoTitle>
                <VideoDescription>{video.description}</VideoDescription>
              </VideoInfo>
            </VideoCard>
          ))}
        </VideosGrid>
      </Content>
    </Container>
  );
};

export default VideoGallery;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.1);
  }
`;

const Container = styled.section`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-dark);
  position: relative;
  padding: 60px 0;
  overflow: hidden;
`;

const BackgroundGlow = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 800px;
  height: 800px;
  border-radius: 50%;
  background: var(--primary-color);
  filter: blur(200px);
  opacity: 0.1;
  animation: ${pulse} 8s ease-in-out infinite;
  pointer-events: none;
`;

const Content = styled.div`
  width: 90%;
  max-width: 1300px;
  position: relative;
  z-index: 1;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 40px;
  opacity: 0;
  
  ${({ $isVisible }) => $isVisible && css`
    animation: ${fadeInUp} 0.8s ease forwards;
  `}
`;

const SectionBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background: rgba(229, 168, 0, 0.1);
  border: 1px solid rgba(229, 168, 0, 0.2);
  border-radius: 50px;
  padding: 10px 25px;
  margin-bottom: 25px;

  span {
    font-family: 'Rajdhani', sans-serif;
    font-weight: 600;
    font-size: 0.85rem;
    color: var(--primary-color);
    letter-spacing: 3px;
  }
`;

const Title = styled.h2`
  font-family: 'Orbitron', sans-serif;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  color: #fff;
  margin-bottom: 15px;
`;

const HighlightText = styled.span`
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  font-family: 'Rajdhani', sans-serif;
  font-size: 1.1rem;
  color: #888;
  max-width: 600px;
  margin: 0 auto;
`;

const VideosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;

  @media (max-width: 1000px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 650px) {
    grid-template-columns: 1fr;
    max-width: 500px;
    margin: 0 auto;
  }
`;

const VideoCard = styled.div`
  position: relative;
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
  border-radius: 20px;
  overflow: hidden;
  opacity: 0;
  transition: all 0.4s ease;
  
  ${({ $isVisible, $delay }) => $isVisible && css`
    animation: ${scaleIn} 0.6s ease forwards;
    animation-delay: ${$delay};
  `}

  &:hover {
    transform: translateY(-10px);
    border-color: var(--primary-color);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3),
                0 0 30px var(--primary-glow);
  }

  &:hover .play-overlay {
    opacity: 0;
  }
`;

const CardGlow = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 200px;
  height: 200px;
  background: var(--primary-color);
  filter: blur(80px);
  opacity: 0;
  transition: opacity 0.4s ease;
  pointer-events: none;
  z-index: 0;
  
  ${VideoCard}:hover & {
    opacity: 0.2;
  }
`;

const VideoWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 9/16;
  background: #000;
  overflow: hidden;
`;

const StyledVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const PlayOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.4);
  transition: opacity 0.3s ease;
  pointer-events: none;
`;

const PlayButton = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: var(--gradient-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000;
  font-size: 1.5rem;
  box-shadow: 0 10px 30px var(--primary-glow);
  
  svg {
    margin-left: 5px;
  }
`;

const VideoInfo = styled.div`
  padding: 20px;
  position: relative;
  z-index: 1;
`;

const VideoTitle = styled.h3`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.3rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 8px;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const VideoDescription = styled.p`
  font-family: 'Rajdhani', sans-serif;
  font-size: 0.95rem;
  color: #888;
`;

